import { useTranslation } from '../../core/hooks/use-translation';
import { Card } from '../../ui/card';
import { Skeleton } from '../../ui/skeleton';

/**
 * The portal's shape while its reads are in flight.
 *
 * **One line of grey text was the whole of the old loading state**, on an otherwise empty page, for
 * a screen that makes several requests before it can draw. That looks the same as a session that
 * has quietly ended. This draws the layout that is coming — the band, the tab bar, a section and the
 * contact card — so the page does not jump when it arrives and a slow connection looks slow rather
 * than broken.
 *
 * The live region is on the container and the shapes inside it are hidden: a screen reader is told
 * once that the page is loading, rather than read a list of rectangles.
 */
export function PortalSkeleton() {
    const { t } = useTranslation();

    return (
        <div role="status" aria-live="polite" className="mx-auto max-w-6xl px-4 py-6 sm:py-12">
            <span className="sr-only">{t('portal.loading')}</span>

            <Skeleton className="h-40 rounded-3xl" />

            <Skeleton className="mt-6 h-12 rounded-2xl" />

            <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start">
                <Card>
                    <div className="space-y-4 p-5">
                        <Skeleton className="h-5 w-40" />
                        <Skeleton className="h-24" />
                        <Skeleton className="h-24" />
                    </div>
                </Card>

                <Card>
                    <div className="space-y-4 p-5">
                        <Skeleton className="h-5 w-28" />
                        <Skeleton className="h-10" />
                        <Skeleton className="h-10" />
                    </div>
                </Card>
            </div>
        </div>
    );
}
